import {Button, Card, CardBody, CardHeader, Divider, Tabs, Tab} from "@nextui-org/react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import useSWR from "swr";
import { getFetcher } from "../../../../services/api";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import CustomDataGrid from "../../../../components/elements/CustomDataGrid";
import { isiSel } from "../../../../services/angkaTabel";
import React, { useMemo, useState } from "react";
import {format} from "date-fns";

export default function PengangkutanPencarian() {
    const currentDate = new Date();
    const formattedDate = format(currentDate, 'dd-MM-yyyy');

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    // Bawaan hari-ini-sampai-hari-ini praktis tidak pernah ada laporannya,
    // jadi hasil pencarian selalu kosong saat halaman dibuka. Sama seperti
    // halaman grafik: awal tahun berjalan sampai hari ini.
    const awalTahun = format(new Date(currentDate.getFullYear(), 0, 1), 'dd-MM-yyyy');
    const [startDate, setStartDate] = useState(awalTahun);
    const [endDate, setEndDate] = useState(formattedDate);
    const [keyword, setKeyword] = useState('');

    const swrOptions = {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
        /*
         * revalidateOnMount HARUS true. Sebelumnya false, sehingga halaman
         * tidak pernah memanggil API saat dibuka - kedua tabel selalu kosong
         * dan baru terisi bila tombol Refresh ditekan. Terbukti dari catatan
         * jaringan: nol permintaan saat buka, tiga permintaan setelah Refresh.
         * (fallbackData: null juga dibuang karena membuat SWR menganggap data
         * awal sudah tersedia.)
         */
        revalidateOnMount: true,
        dedupingInterval: 60000,
        suspense: false,
    };

    /*
     * Nomor halaman dan jumlah baris harus ikut ke dalam alamat. Sebelumnya
     * keduanya tidak dikirim sama sekali, sehingga server memakai bawaannya
     * (halaman 1, 10 baris) dan menekan tombol halaman berikutnya tidak
     * mengambil apa pun - kuncinya tidak berubah, jadi SWR memakai data lama.
     */
    const halaman = page + 1; // DataGrid menghitung dari 0, API dari 1

    const { data: reportedB3, mutate: mutateReportedb3 } = useSWR(
        `/api/dashboard/pelaporan-pengangkutan/pencarian/bahanb3?page=${halaman}&limit=${pageSize}&startDate=${startDate}&endDate=${endDate}&keyword=${keyword}`,
        getFetcher,
        swrOptions
    );

    const { data: companies, mutate: mutateCompanies } = useSWR(
        `/api/dashboard/pelaporan-pengangkutan/pencarian/perusahaan?page=${halaman}&limit=${pageSize}&startDate=${startDate}&endDate=${endDate}&keyword=${keyword}`,
        getFetcher,
        swrOptions
    );

    /*
     * Tab "Jenis B3" dulu memakai columnsPerusahaan, padahal barisnya berisi
     * bahan kimia - seluruh kolomnya tidak pernah cocok sehingga tabel tampak
     * kosong walau datanya ada. Kolomnya kini sesuai bentuk datanya, lengkap
     * dengan nomor CAS.
     */
    /*
     * Baris hasil pencarian B3 tidak punya kolom id, sedangkan DataGrid
     * mewajibkannya. Gabungan nomor CAS dan nama bahan sudah unik karena
     * kuerinya mengelompokkan keduanya.
     */
    const pengenalBarisB3 = (row) => `${row.casNumber}-${row.namaBahanKimia}`;

    const columnsB3 = useMemo(() => [
        {
            field: 'casNumber',
            headerName: 'Nomor CAS',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.casNumber || '-'}</span>,
        },
        {
            field: 'namaBahanKimia',
            headerName: 'Nama Bahan Kimia',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.namaBahanKimia || '-'}</span>,
        },
        {
            field: 'namaDagang',
            headerName: 'Nama Dagang',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.namaDagang || '-'}</span>,
        },
        {
            field: 'tipeBahan',
            headerName: 'Tipe Bahan',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.tipeBahan || '-'}</span>,
        },
        {
            field: 'sum',
            headerName: 'Jumlah Diangkut',
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => <span className="tabular-nums">{isiSel('jumlah', params?.row?.sum)}</span>,
        },
    ], []);

    const columnsPerusahaan = useMemo(() => [
        {
            field: 'name',
            headerName: 'Nama Perusahaan',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.name || '-'}</span>,
        },
        {
            field: 'nomorInduk',
            headerName: 'Nomor Induk',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.nomorInduk || '-'}</span>,
        },
        {
            field: 'penanggungJawab',
            headerName: 'Penanggung Jawab',
            flex: 1,
            renderCell: (params) => <span>{params?.row?.penanggungJawab || '-'}</span>,
        },
        {
            field: 'total',
            headerName: 'Total',
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => <span className="tabular-nums">{isiSel('jumlah', params?.row?.total)}</span>,
        },
    ], []);

    const handleRefresh = () => {
        mutateReportedb3();
        mutateCompanies();
    };

    const handleGetData = () => {
        mutateReportedb3();
        mutateCompanies();
    };

    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
    };

    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
    };

    const handleChange = (event) => {
        setKeyword(event.target.value);
    };

    return (
        <RootAdmin>
            <Card className="w-full" radius="sm">
                <CardHeader className="flex justify-between items-center">
                    <p className="text-md">Dashboard Pencarian Pelaporan Pengangkutan B3</p>
                    <div className="flex gap-2">
                        <Button size="sm" color="primary" onPress={handleGetData}>
                            Get Data
                        </Button>
                        <Button size="sm" color="secondary" startContent={<ArrowPathIcon className="size-4" />} onPress={handleRefresh}>
                            Refresh
                        </Button>
                    </div>
                </CardHeader>
                <Divider />
                <CardBody className="w-full h-[550px] p-5">
                    <Filter
                        startDate={startDate}
                        handleStartDate={handleStartDateChange}
                        endDate={endDate}
                        handleEndDate={handleEndDateChange}
                        keyword={keyword}
                        handleKeyword={handleChange}
                    />
                    <Tabs aria-label="Pelaporan Pencarian B3" defaultValue="reportedB3">
                        <Tab title="Pelaporan Berdasarkan Jenis B3" value="reportedB3">
                            <CustomDataGrid
                                data={reportedB3?.data || []}
                                rowCount={reportedB3?.total || 0}
                                columns={columnsB3}
                                getRowId={pengenalBarisB3}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                page={page}
                                setPage={setPage}
                            />
                        </Tab>
                        <Tab title="Pelaporan Berdasarkan Perusahaan" value="companies">
                            <CustomDataGrid
                                data={companies?.data || []}
                                rowCount={companies?.total || 0}
                                columns={columnsPerusahaan}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                page={page}
                                setPage={setPage}
                            />
                        </Tab>
                    </Tabs>
                </CardBody>
            </Card>
        </RootAdmin>
    );
}

function Filter({ startDate, handleStartDate, endDate, handleEndDate, keyword, handleKeyword }) {
    return(
        <div className="flex items-center gap-3 mb-10">
            <div className="flex flex-row w-1/8 border-b-2 border-bottom-gray">
                <div className="flex items-center mr-5">
                    <span>Start Date:</span>
                </div>
                <input
                    type="date"
                    id="date-input"
                    value={startDate}
                    onChange={handleStartDate}
                />
            </div>
            <div className="flex flex-row w-1/8 border-b-2 border-bottom-gray">
                <div className="flex items-center mr-5">
                    <span>End Date:</span>
                </div>
                <input
                    type="date"
                    id="date-input"
                    value={endDate}
                    onChange={handleEndDate}
                />
            </div>
            <div className="flex flex-row w-1/8 border-b-2 border-bottom-gray">
                <div className="flex items-center mr-5">
                    <span>Keyword:</span>
                </div>
                <input
                    type="input"
                    id="input"
                    value={keyword}
                    onChange={handleKeyword}
                />
            </div>
        </div>
    )
}
